import { isSameYearInTZ } from '@dte/otw-utils-dates/src/compare/isSameYear';
import { DatePrecision } from '@dte/otw-utils-dates/src/DatePrecision';
import { formatEST } from '@dte/otw-utils-dates/src/format/formatEST';
import { safeParseDate } from '@dte/otw-utils-dates/src/parse/date';
import type { ReactNode } from 'react';
import { isDateValue } from '../History/Action/isDateValue';

export function formatTime(dateString: string): string | undefined {
	// Return undefined for date-only strings
	if (dateString?.length <= 10) {
		return undefined;
	}

	const date = safeParseDate(dateString);
	if (!date) {
		return undefined;
	}

	return formatEST(date, 'h:mm a');
}

export function formatShortDate(dateString: string): string | undefined {
	const date = safeParseDate(dateString);
	if (!date) {
		return undefined;
	}

	// Include the year if not the current year
	const sameYear = isSameYearInTZ(date);
	if (!sameYear) {
		return formatEST(date, 'M/d/yy');
	}

	return formatEST(date, 'M/d');
}

export function formatShortDateTime(dateString: string): string | undefined {
	const date = safeParseDate(dateString);
	if (!date) {
		return undefined;
	}

	// If we have an ISO date with only a date portion, just return that
	if (dateString.length <= 10) {
		return formatShortDate(dateString);
	}

	return `${formatShortDate(dateString)} at ${formatTime(dateString)}`;
}

export function formatLongDate(dateString: string): string {
	const date = safeParseDate(dateString);
	if (!date) {
		return undefined;
	}

	return formatEST(date, 'EEEE, MMMM d, yyyy');
}

export function formatLongDateTime(dateString: string): string {
	const date = safeParseDate(dateString);
	if (!date) {
		return undefined;
	}

	const time = formatTime(dateString);
	const dayOfMonth = formatEST(date, 'MMMM d, yyyy');

	return `${dayOfMonth} at ${time}`;
}

export interface DateTimeProps {
	date?: string;
	previousDate?: string;
	dateFormat?: DatePrecision;
	colSpan?: number;
}

export function ShortDateTime(props: DateTimeProps): ReactNode {
	let content = <>-</>;

	const dateString = props.date;
	if (isDateValue(dateString)) {
		content = (
			<>
				<span className="date">{formatShortDate(dateString)}</span>
				{' at '}
				<span className="time">{formatTime(dateString)}</span>
			</>
		);
	}

	return <span style={{ whiteSpace: 'nowrap' }}>{content}</span>;
}

export function ShortDate(props: DateTimeProps): ReactNode {
	let content = <>-</>;

	const dateString = props.date;
	if (dateString) {
		const formatted = formatShortDate(dateString);

		const previousDate = props.previousDate;
		const previousFormatted = formatShortDate(previousDate);

		// Skip showing the date if it was the same as the previous value
		if (formatted === previousFormatted) {
			content = <>&nbsp;</>;
		} else {
			content = (
				<>
					<span className="date">{formatShortDate(dateString)}</span>
				</>
			);
		}
	}

	return content;
}

export function ShortTime(props: DateTimeProps): ReactNode {
	let content: ReactNode;

	// Skip showing time for day format
	if (props.dateFormat === DatePrecision.Day) {
		content = <>End of Day</>;
	}

	const dateString = props.date;
	if (dateString) {
		content = (
			<>
				<span className="date">{formatTime(dateString)}</span>
			</>
		);
	}

	return content;
}

export function ShortDateTimeCells(props: DateTimeProps): ReactNode {
	return (
		<>
			<td className="dateTime date">
				<ShortDate {...props} />
			</td>
			<td className="dateTime time">
				<ShortTime {...props} />
			</td>
		</>
	);
}
